import { useCallback, useEffect, useState } from "react";
import { CheckCircle } from "lucide-react";
import { useApplication } from "@/contexts/ApplicationContext";
import { TabsContent } from "@/components/ui/tabs";
import VehicleCollectionTabs from "./vehicle-collection/VehicleCollectionTabs";
import VehicleBooking from "./VehicleBooking";
import VehicleCollection from "./VehicleCollection";

const VehicleCollectionForm = () => {
  const { application, refetch } = useApplication();
  const [activeTab, setActiveTab] = useState("payment-info");
  const [submittedTabs, setSubmittedTabs] = useState<string[]>([]);

  // Helper function to get completed tabs based on application data
  const getCompletedTabs = useCallback(() => {
    const completedTabs = [];

    console.log("Checking completion status:", {
      vehicleBooking: application?.vehicleBooking,
      vehicleCollection: application?.vehicleCollection,
    });

    // Payment info is completed if payment method exists
    if (application?.vehicleBooking?.paymentMethodId) {
      console.log("Payment info is complete");
      completedTabs.push("payment-info");
    }

    // Vehicle booking is completed if collection date exists
    if (application?.vehicleCollection?.date) {
      console.log("Vehicle booking is complete");
      completedTabs.push("vehicle-booking");
    }

    console.log("Final completed tabs:", completedTabs);
    return completedTabs;
  }, [application]);

  // Helper function to find the next incomplete tab
  const findNextIncompleteTab = useCallback((completedTabs: string[]) => {
    const allTabs = ["payment-info", "vehicle-booking"];

    for (const tab of allTabs) {
      if (!completedTabs.includes(tab)) {
        return tab;
      }
    }

    return ""; // All tabs completed
  }, []);

  useEffect(() => {
    if (application?.id) {
      const completedTabs = getCompletedTabs();
      console.log("Completed vehicle collection tabs:", completedTabs);

      // Always update submittedTabs to reflect current application state
      setSubmittedTabs(completedTabs);

      // Prefer backend-provided current segment if it matches a visible tab
      const backendCurrentSegment =
        (application as unknown as Record<string, unknown>)[
          "current_segment"
        ] ||
        (application as unknown as Record<string, unknown>)["currentSegment"];

      let newActiveTab = "";
      const visibleTabs = ["payment-info", "vehicle-booking"];

      if (
        typeof backendCurrentSegment === "string" &&
        visibleTabs.includes(backendCurrentSegment)
      ) {
        newActiveTab = backendCurrentSegment;
      } else {
        newActiveTab = findNextIncompleteTab(completedTabs);
      }

      console.log("Setting active vehicle collection tab:", newActiveTab);
      console.log("Application data:", {
        paymentMethodId: application.vehicleBooking?.paymentMethodId,
        collectionDate: application.vehicleCollection?.date,
      });
      setActiveTab(newActiveTab);
    }
  }, [
    application?.id,
    application.vehicleBooking?.paymentMethodId,
    application.vehicleCollection?.date,
    getCompletedTabs,
    findNextIncompleteTab,
    application,
  ]);

  const handleTabChange = (value: string) => {
    setActiveTab(value);
  };

  // Handle successful payment submission
  const handlePaymentComplete = async () => {
    // Refetch application data to get updated payment info
    // The useEffect will automatically handle updating submittedTabs and activeTab
    await refetch();
  };

  // Handle successful vehicle booking submission
  const handleBookingComplete = async () => {
    // Refetch application data to get updated booking info
    // The useEffect will automatically handle updating submittedTabs and activeTab
    await refetch();
  };

  // Render completion message if all tabs are completed
  if (activeTab === "") {
    return (
      <div className="max-w-3xl mx-auto">
        <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
          <h2 className="text-xl font-semibold text-green-800 mb-2">
            Vehicle Collection Complete
          </h2>
          <p className="text-green-700 mb-4">
            You have successfully completed your payment information and vehicle
            booking. We'll see you at your scheduled collection time!
          </p>
          <div className="flex justify-center">
            <CheckCircle className="h-16 w-16 text-green-500" />
          </div>
        </div>
      </div>
    );
  }

  // Render tabs
  return (
    <div className="max-w-3xl mx-auto">
      <VehicleCollectionTabs
        activeTab={activeTab}
        onTabChange={handleTabChange}
        submittedTabs={submittedTabs}
      >
        <TabsContent value="payment-info">
          <div className="bg-white p-4 md:p-6 rounded-lg shadow-sm">
            <VehicleBooking onComplete={handlePaymentComplete} />
          </div>
        </TabsContent>

        <TabsContent value="vehicle-booking">
          <div className="bg-white p-4 md:p-6 rounded-lg shadow-sm">
            <VehicleCollection onComplete={handleBookingComplete} />
          </div>
        </TabsContent>
      </VehicleCollectionTabs>
    </div>
  );
};

export default VehicleCollectionForm;
